import { useEffect, useCallback, useRef } from 'react';
import { FreelancerProfile } from '../models/FreelancerProfile';

// Déterminer le storage à utiliser selon l'environnement (même logique que auth-config)
const isProd = process.env.REACT_APP_ENVIRONMENT === "production";
const isLocalhost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const useSharedStorage = isProd || isLocalhost;

// Utiliser localStorage en prod/localhost, sessionStorage en recette
const storage = useSharedStorage ? localStorage : sessionStorage;

/**
 * Custom hook to persist talent selection per user and per project
 * Uses localStorage in prod/localhost (shared between tabs)
 * Uses sessionStorage in recette (isolated per tab)
 *
 * @param userId - The current user ID
 * @param projectId - The current project ID
 * @param selectedTalents - The current array of selected talents
 * @param setSelectedTalents - The state setter for selected talents
 * @param availableTalents - All available talents from the search results
 */
export const usePersistentTalentSelection = (
  userId: number | undefined,
  projectId: number | undefined,
  selectedTalents: FreelancerProfile[],
  setSelectedTalents: (talents: FreelancerProfile[]) => void,
  availableTalents: FreelancerProfile[] | undefined
) => {
  // Track if we've loaded from storage
  const hasLoadedRef = useRef(false);

  // Generate unique key for storage
  const getStorageKey = useCallback(() => {
    return projectId
      ? `zoulloo_talent_selection_user_${userId}_project_${projectId}`
      : `zoulloo_talent_search_user_${userId}`;
  }, [userId, projectId]);

  // Load selection from storage on mount
  useEffect(() => {
    // Skip if already loaded
    if (hasLoadedRef.current) {
      return;
    }

    if (!userId || !availableTalents || availableTalents.length === 0) {
      return;
    }

    const storageKey = getStorageKey();
    const storedData = storage.getItem(storageKey);

    if (storedData) {
      try {
        const selectedIds: number[] = JSON.parse(storedData);

        // Filter and restore only talents that exist in available talents
        const restoredTalents = availableTalents.filter(talent =>
          selectedIds.includes(talent.id)
        );

        if (restoredTalents.length > 0) {
          setSelectedTalents(restoredTalents);
        }
      } catch (error) {
        console.error('Error parsing stored talent selection:', error);
        // Clear invalid data
        storage.removeItem(storageKey);
      }
    }

    // Mark as loaded
    hasLoadedRef.current = true;

    // Only run once when availableTalents becomes available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTalents?.length]);

  // Save selection to storage whenever it changes
  useEffect(() => {
    // Don't save until we've loaded from storage
    if (!hasLoadedRef.current) {
      return;
    }

    if (!userId) {
      return;
    }

    const storageKey = getStorageKey();

    if (selectedTalents.length === 0) {
      // Remove from storage if no selection
      storage.removeItem(storageKey);
    } else {
      // Save only the IDs to storage
      const selectedIds = selectedTalents.map(talent => talent.id);
      storage.setItem(storageKey, JSON.stringify(selectedIds));
    }
  }, [userId, selectedTalents, getStorageKey]);

  // Function to clear the persisted selection
  const clearPersistedSelection = useCallback(() => {
    if (!userId) {
      return;
    }

    const storageKey = getStorageKey();
    storage.removeItem(storageKey);
  }, [userId, getStorageKey]);

  return {
    clearPersistedSelection
  };
};
